import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Reveal } from "./Reveal";

export const FeaturedProject = () => {
  const { lang, t } = useLanguage();
  const [project, setProject] = useState(null);

  useEffect(() => {
    api
      .get("/projects")
      .then((r) => setProject(r.data.find((p) => p.featured) || null))
      .catch(() => {});
  }, []);

  if (!project) return null;

  const desc = lang === "pt" ? project.description_pt : project.description_en;
  const waHref = `https://wa.me/351916927419?text=${encodeURIComponent(`${t.featured.waText} ${project.name}`)}`;
  const meta = [project.location, project.year, project.typology].filter(Boolean).join("  ·  ");

  return (
    <section className="border-y border-white/10 bg-surface/40 py-24 sm:py-32" data-testid="featured-project">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold" data-testid="featured-eyebrow">
            {t.featured.eyebrow}
          </p>
        </Reveal>

        <div className="mt-10 grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <Link
              to={`/projetos/${project.id}`}
              className="group relative block overflow-hidden rounded-lg border border-white/10"
              data-testid="featured-image-link"
            >
              <img
                src={project.image}
                alt={project.name}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                data-testid="featured-image"
              />
              <span className="absolute left-4 top-4 rounded-full border border-[#D4AF3766] bg-basalt/75 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold backdrop-blur-sm">
                {t.featured.forSale}
              </span>
            </Link>
          </Reveal>

          <div>
            <Reveal delay={0.1}>
              <h2 className="font-display text-2xl font-normal leading-snug tracking-tight text-sand sm:text-3xl lg:text-4xl" data-testid="featured-title">
                {project.name}
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-sand/60" data-testid="featured-meta">
                {meta}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-6 max-w-lg text-sm leading-relaxed text-mist sm:text-base" data-testid="featured-desc">
                {desc}
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to={`/projetos/${project.id}`}
                  data-testid="featured-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-medium text-basalt transition-colors duration-300 hover:bg-sand"
                >
                  {t.featured.cta} <ArrowUpRight size={15} />
                </Link>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="featured-whatsapp"
                  className="inline-flex items-center gap-2 rounded-full border border-sand/30 px-7 py-3.5 text-sm font-medium text-sand transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
                >
                  <MessageCircle size={15} /> {t.featured.whatsapp}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
